import React, { useContext } from 'react';

import { TrackingContext } from '../../TrackingContext';
import ThemeInfoVerticalExperiment, { MatchingInvitations } from './ThemeInfo';
import verticalStyles from './themeCard.scss';
import { ThemeCardContainer } from './ThemeCardComponents/ThumbnailContainers';
import { findThumbnailTheme } from './ThemeCardComponents/util';
import classNames from 'classnames';
import ThemeLayoutSelector from './ThemeCardComponents/ThemeLayoutSelector/ThemeLayoutSelector';
import useBuildThemeAnalyticsBaseProps from '../../hooks/useBuildThemeAnalyticsBaseProps';

export type Theme = {
  id?: number | null;
  name?: string | null;
  family?: string | null;
  previewImage?: { mediaApiId: string } | null;
  mobilePreviewImage?: { mediaApiId: string } | null;
  stationaryPreviewImage?: { mediaApiId: string } | null;
  swatchImage?: { mediaApiId: string } | null;
  designerUrl?: string | null;
  themeDesigner?: { name?: string | null; companyName?: string | null } | null;
  active?: boolean | null;
};

export type ThemeFamily = {
  family: string | null;
  themes: Theme[] | null;
};

export interface ThemeCardProps {
  themeFamily: ThemeFamily | null;
  onClick?: (themeId: number, theme?: Theme) => void;
  selected?: number | null;
  featured?: number | null;
  dashboard?: boolean | null;
  hideInvitationLink?: boolean | null;
  children?: React.ReactNode;
}

const ThemeCard: React.FC<ThemeCardProps> = ({
  themeFamily,
  onClick,
  selected = null,
  featured = null,
  dashboard = false,
  hideInvitationLink = false,
}) => {
  const { track } = useContext(TrackingContext);
  const { getThemeAnalyticsBaseProps } = useBuildThemeAnalyticsBaseProps();

  if (!themeFamily?.themes) {
    return null;
  }

  // By default, primary theme is the first theme in the family
  const theme = themeFamily.themes[0];

  const thumbnailTheme = findThumbnailTheme({
    featured,
    selected,
    theme,
    themeFamily,
  });

  const thumbnailClickHandler = () => {
    if (onClick && !selected && !featured && thumbnailTheme?.id) {
      onClick(thumbnailTheme?.id);
      track('Design Previewed', {
        ...getThemeAnalyticsBaseProps(thumbnailTheme),
        selection: 'theme thumbnail',
      });
    }
  };

  return (
    <div
      className={classNames(verticalStyles.themeCard, {
        [verticalStyles.dashboardCard]: dashboard,
        [verticalStyles.featured]: featured,
      })}
      data-testid={`theme${featured ? '-current' : ''}-${thumbnailTheme?.id}`}
    >
      <div
        className={classNames({
          [verticalStyles.themeCard__dashboard__container]: dashboard,
          [verticalStyles.themeCard__featured__container]: featured,
        })}
      >
        <ThemeCardContainer
          clickHandler={thumbnailClickHandler}
          theme={thumbnailTheme}
          selected={selected}
          featured={featured}
          type="desktop"
        />
        {!featured && <ThemeCardContainer type="mobile" clickHandler={thumbnailClickHandler} theme={thumbnailTheme} />}
        <div className={verticalStyles.themeCard__controlsWrapper}>
          <ThemeInfoVerticalExperiment
            themeFamily={themeFamily}
            thumbnailTheme={thumbnailTheme}
            onClick={onClick}
            featured={featured}
            selected={selected}
            hideInvitationLink={hideInvitationLink}
          />
          {featured && (
            <div className={verticalStyles.themeCard__ThemeLayoutSelectorWrapper}>
              <ThemeLayoutSelector />
            </div>
          )}
        </div>
      </div>
      {featured && <MatchingInvitations theme={thumbnailTheme} />}
    </div>
  );
};

export default ThemeCard;
